/**
 * Confetti that lands on whatever page you arrived at.
 *
 * The launch screen used to throw its confetti and then navigate, so the burst
 * played over the screen you were leaving. The celebration belongs on the
 * destination: you press Launch, the Intelligence home comes up, and the
 * confetti falls across it.
 *
 * That means the burst has to outlive the route change, so it cannot live in
 * the page that starts it. This component mounts once, above the router, and
 * listens for a signal. `requestCelebration()` is a plain module-level flag and
 * a DOM event rather than router state: navigation is client-side, so module
 * state survives it, and nothing has to be threaded through the route.
 *
 * Non-interactive by construction — `pointer-events: none` — so the page
 * underneath is usable the instant it renders, even mid-burst.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { BRAND, ZAMBIA } from '../../theme/colors';

const CELEBRATE_EVENT = 'phsims:celebrate';

/** How long the celebration runs once the destination is up. */
const DURATION_MS = 5000;

/**
 * Volleys, in milliseconds from the press.
 *
 * A single burst cannot fill five seconds however long the timer says: thrown
 * pieces clear the screen in roughly two, and simply extending the clock leaves
 * four seconds of empty canvas. Firing repeatedly keeps confetti arriving, so
 * the last pieces are still falling when the celebration ends.
 *
 * The final volley is at 3s rather than later, so nothing is left hanging
 * mid-air when the canvas tears down.
 */
const VOLLEYS_MS = [0, 700, 1400, 2100, 3000];

/** Pieces per corner cannon, per volley. */
const PER_SIDE = 130;

const INK = '#F2F7F4';

interface Piece {
  x: number; y: number;
  vx: number; vy: number;
  spin: number; angle: number;
  life: number; max: number;
  size: number; color: string;
}

/** Ask for confetti on the next page. Safe to call before navigating. */
export function requestCelebration(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(CELEBRATE_EVENT));
}

export function LaunchConfetti() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const piecesRef = useRef<Piece[]>([]);
  const frameRef = useRef<number | null>(null);
  /** True once the last volley has fired. Until then an empty canvas is a gap
   *  between volleys, not the end of the celebration. */
  const doneFiringRef = useRef(false);
  const [active, setActive] = useState(false);

  /** Fire one volley from both corner cannons. Appends, never replaces, so
   *  volleys overlap rather than cutting each other off. */
  const spawn = useCallback((power = 1) => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const colours = [BRAND.orange, BRAND.orangeLight, ZAMBIA.forestLight, INK, BRAND.orange];
    const pieces = piecesRef.current;

    // Two cannons at the lower corners firing inward and up, rather than one
    // burst from the middle: the centre of the screen is where the page's own
    // content is, and confetti should frame it, not cover it.
    const sides = [
      { x: w * 0.03, y: h * 1.0, dir: 1 },
      { x: w * 0.97, y: h * 1.0, dir: -1 },
    ];
    sides.forEach(({ x, y, dir }) => {
      for (let i = 0; i < Math.round(PER_SIDE * power); i += 1) {
        // Aim between roughly 50 and 80 degrees above horizontal, inward.
        const angle = (-Math.PI / 2) + dir * (0.16 + Math.random() * 0.52);
        // Enough to clear the top of a tall screen; squared random keeps the
        // spread weighted toward the middle of the arc.
        const speed = 17 + Math.random() * Math.random() * 24;
        pieces.push({
          x, y,
          vx: Math.cos(angle) * speed * dir * -1,
          vy: Math.sin(angle) * speed,
          spin: (Math.random() - 0.5) * 0.4,
          angle: Math.random() * Math.PI,
          life: 0,
          max: 260 + Math.random() * 160,
          size: 9 + Math.random() * 12,
          color: colours[i % colours.length],
        });
      }
    });
  }, []);

  const tick = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (canvas.width !== Math.floor(w * dpr)) {
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    ctx.clearRect(0, 0, w, h);

    const pieces = piecesRef.current;
    for (let i = pieces.length - 1; i >= 0; i -= 1) {
      const p = pieces[i];
      p.life += 1;
      if (p.life >= p.max || p.y > h + 60) {
        pieces.splice(i, 1);
        continue;
      }
      p.vy += 0.30;      // gravity, eased so pieces hang a little longer
      p.vx *= 0.994;     // drag
      p.x += p.vx;
      p.y += p.vy;
      p.angle += p.spin;

      const t = p.life / p.max;
      ctx.globalAlpha = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.restore();
    }
    ctx.globalAlpha = 1;

    // Keep drawing while pieces remain, or while volleys are still to come —
    // an empty canvas between volleys is a gap, not the end.
    if (pieces.length || !doneFiringRef.current) {
      frameRef.current = requestAnimationFrame(tick);
    } else {
      setActive(false);
    }
  }, []);

  useEffect(() => {
    const onCelebrate = () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      setActive(true);
    };
    window.addEventListener(CELEBRATE_EVENT, onCelebrate);
    return () => window.removeEventListener(CELEBRATE_EVENT, onCelebrate);
  }, []);

  useEffect(() => {
    if (!active) return;

    doneFiringRef.current = false;
    piecesRef.current = [];

    // The opening volley is heaviest; the rest keep the screen filled so the
    // five seconds read as one continuous celebration rather than a burst
    // followed by four seconds of nothing.
    const timers = VOLLEYS_MS.map((delay, i) =>
      window.setTimeout(() => {
        spawn(i === 0 ? 1.6 : 1);
        if (i === VOLLEYS_MS.length - 1) doneFiringRef.current = true;
      }, delay),
    );

    frameRef.current = requestAnimationFrame(tick);

    // Hard stop, so a backgrounded tab that never finishes the physics still
    // tears the canvas down instead of leaving it over the page.
    const stop = window.setTimeout(() => {
      piecesRef.current = [];
      doneFiringRef.current = true;
      setActive(false);
    }, DURATION_MS);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      timers.forEach(window.clearTimeout);
      window.clearTimeout(stop);
    };
  }, [active, spawn, tick]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 21474836,
      }}
    />
  );
}

export default LaunchConfetti;
